import { type ReactNode } from 'react';
import { Portal } from '@headlessui/react';
import { useTransition, animated, config } from '@react-spring/web';
import clsx from 'clsx';
import { useToaster, type Toast } from 'react-hot-toast/headless';

import styles from './ToastContainer.module.css';

const DEFAULT_TOAST_DURATION = 5000;

type ToastPositionVariant = 'bottom-center' | 'top-center' | 'relative';

const getPositionClassName = (position: ToastPositionVariant) => {
  switch (position) {
    case 'bottom-center':
      return styles['bottom-center'];
    case 'top-center':
      return styles['top-center'];
    default:
      return '';
  }
};

interface Props {
  testid?: string;
  position?: ToastPositionVariant;
  duration?: number;
  containerClassName?: string;
}

interface RenderedToastProps {
  toast: Toast;
}

function RenderedToast(props: RenderedToastProps) {
  const { toast } = props;
  const { visible, ariaProps, id: toastId, message: toastMessage } = toast;
  const transitions = useTransition(visible, {
    config: config.stiff,
    from: {
      transform: `translate3d(0, 100%, 0)`,
      opacity: 0,
    },
    enter: {
      transform: `translate3d(0, 0%, 0)`,
      opacity: 1,
    },
    leave: {
      transform: `translate3d(0, 100%, 0)`,
      opacity: 0,
    },
  });

  return transitions(({ opacity, transform }, item) => {
    if (item === false) {
      return null;
    }

    return (
      <animated.div
        className={styles.toast}
        {...ariaProps}
        style={{ transform, opacity }}
        key={toastId}
      >
        {toastMessage as ReactNode}
      </animated.div>
    );
  });
}

export function ToastContainer(props: Props) {
  const {
    testid,
    duration,
    position = 'bottom-center',
    containerClassName,
  } = props;
  const { toasts } = useToaster({
    duration: typeof duration === 'number' ? duration : DEFAULT_TOAST_DURATION,
  });

  const className =
    position === 'bottom-center' || position === 'top-center'
      ? styles.fixed
      : styles.none;

  const body = (
    <div
      data-testid={testid}
      className={clsx(
        {
          [styles['has-toasts-to-render']]: toasts.length > 0,
        },
        className,
        getPositionClassName(position),
        containerClassName
      )}
    >
      {toasts.map((toast) => {
        return <RenderedToast key={toast.id} toast={toast} />;
      })}
    </div>
  );

  if (position !== 'relative') {
    return <Portal data-testid="toasts-container">{body}</Portal>;
  }

  return body;
}
